import React, {useCallback, useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Trans, useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import classnames from "classnames";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import VendorAutocomplete from "../../text-fields/autocomplete/vendor-autocomplete";
import {routeFunctions} from "../../../../core/models/static/routes/app-routes";
import Utils from "../../../../core/services/utils/utils";

/**
 * Closes the dialog via either setOpen (quote-style) or close (legacy prop).
 */
const dismissDialog = (setOpen, close) => {
    if (typeof setOpen === 'function') {
        setOpen(false);
        return;
    }
    close?.();
};

/**
 * Create-purchase dialog — same flow/styling as add-quote, with vendor in place of customer.
 *
 * @param {boolean} open
 * @param {function(boolean): void} [setOpen]
 * @param {function(): void} [close] legacy close handler when setOpen is omitted
 * @param {object|null} [vendor] optional vendor preselection (e.g. current vendor scope)
 */
const AddPurchaseDialog = ({open, setOpen, close, vendor}) => {
    const history = useHistory();
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.add-purchase'});
    const [submitting, setSubmitting] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(vendor ?? null);
    const isMounted = useIsMounted();

    console.log(open, submitting, !selectedVendor);

    /**
     * Seeds the selected vendor when the dialog opens (and resets submitting).
     */
    useEffect(() => {
        if (!open) {
            return;
        }
        setSelectedVendor(vendor ?? null);
        setSubmitting(false);
    }, [open, vendor]);

    /**
     * Shell create-purchase then navigate to vendor PO single.
     * // TODO: live create purchase API
     */
    const createPurchase = useCallback(async () => {
        if (!selectedVendor?.id) {
            return;
        }
        setSubmitting(true);
        await Utils.wait(250);
        if (!isMounted()) {
            return;
        }
        const purchaseId = 1;
        setSubmitting(false);
        dismissDialog(setOpen, close);
        history.push(routeFunctions.vendor.purchases.orders.single(selectedVendor.id, purchaseId));
    }, [close, history, isMounted, selectedVendor?.id, setOpen]);

    /**
     * Closes without creating a purchase.
     */
    const onClose = useCallback(() => {
        if (submitting) {
            return;
        }
        dismissDialog(setOpen, close);
    }, [close, setOpen, submitting]);

    const vendorPreselected = !!vendor?.id
        && !!selectedVendor?.id
        && vendor.id === selectedVendor.id;

    return (
        <Dialog
            open={open}
            setOpen={setOpen ?? ((nextOpen) => {
                if (!nextOpen) {
                    close?.();
                }
            })}
            maxWidth={'sm'}
            className={'bound-dialog'}
        >
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={'text-lg text-color-1 p-4 font-weight-semi-bold text-center'}>
                    {t('title')}
                </p>
                <Divider className={'divider'}/>
            </DialogTitle>
            <DialogContent className={classnames('px-3')}>
                <div className={'my-3 w-100 d-flex flex-column'}>
                    <p className={'text-sm text-start text-color-1'}>
                        {t('selectPrompt')}
                    </p>
                    {
                        vendorPreselected &&
                        <p className={'text-sm text-start text-color-1 mt-2'}>
                            <Trans
                                t={t}
                                i18nKey={'autoSelected'}
                                values={{name: vendor?.name ?? vendor?.code ?? ''}}
                                components={{
                                    highlight: <span className={'tertiary-main font-weight-semi-bold'}/>,
                                }}
                            />
                        </p>
                    }
                    <div className={'mt-3'}/>
                    <VendorAutocomplete
                        disabled={submitting}
                        value={selectedVendor}
                        onChange={(_, next) => setSelectedVendor(next)}
                        label={t('vendorLabel')}
                        fullWidth
                    />
                </div>
            </DialogContent>
            <DialogActions className={'d-flex flex-column px-0'}>
                <Divider className={'divider'}/>
                <Row className={'no-gutters justify-content-between align-items-start w-100 ml-0'}>
                    <Button
                        disabled={!open || submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={onClose}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {submitting ? t('processing') : t('close')}
                    </Button>
                    <Col className={'row no-gutters justify-content-end align-items-start w-100'}>
                        <Button
                            disabled={!open || submitting || !selectedVendor?.id}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => void createPurchase()}
                            borderRadius={'0 0 10px 0'}
                        >
                            {submitting ? t('processing') : t('create')}
                        </Button>
                    </Col>
                </Row>
            </DialogActions>
        </Dialog>
    );
};

export default AddPurchaseDialog;
